import { checkIsBusinessUser } from "selectors/user";
import { getActiveOrganization } from "selectors/organizations";
import {
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getCurrentZohoOrganizationId,
  getSelectedBusinessData,
  getSelectedBusinessLocations
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AddressField from "./lib/AddressField";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import AmountBaseField from "./lib/AmountBaseField";
import AmountField from "./lib/AmountField";
import AmountVatField from "./lib/AmountVatField";
import Async from "utils/Async";
import BusinessesActions from "actions/BusinessesActions";
import Countries from "const/Countries";
import CurrencyField from "./lib/CurrencyField";
import DetailedTaxRateField from "./lib/DetailedTaxRateField";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import DueDateField from "./lib/DueDateField";
import ExportAsField from "./lib/ExportAsField";
import IdField from "./lib/IdField";
import IssueDateField from "./lib/IssueDateField";
import LocationField from "./lib/LocationField";
import MemoField from "./lib/MemoField";
import PairedTransactionsField from "./lib/PairedTransactionsField";
import PayToField from "./lib/PayToField";
import PaymentDateField from "./lib/PaymentDateField";
import React, { useEffect } from "react";
import TypeField from "./lib/TypeField";
import VatIdField from "./lib/VatIdField";
import useDocumentContext from "hooks/useDocumentContext";
import useShowCommonModal from "hooks/useShowCommonModal";

const Fields = () => {
  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const { countryCode } = useSelector(getActiveOrganization);

  const {
    documentState,
    documentData: { paidTransactions = [], transactionIds = [], id: documentId },
    setDocumentState,
    refetchDocumentData
  } = useDocumentContext();

  const { type, exportAs, detailedTaxRates, paymentType, address } = documentState;

  const showVatIdField = (countryCode === Countries.AU || countryCode === Countries.NZ)
    && paymentType === DocumentPaymentTypes.BUY && !!address?.name?.trim();

  const locations = useSelector(getSelectedBusinessLocations);

  const businessUser = useSelector(checkIsBusinessUser);

  const { settings: { advancedDocumentsWorkflow } = {} } = useSelector(getSelectedBusinessData);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const zohoBusiness = !!useSelector(getCurrentZohoOrganizationId);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const showCommonModal = useShowCommonModal();

  useEffect(() => {
    if (transactionIds?.length && exportAs === AdvancedTransactionTypes.EXPENSE) {
      showCommonModal({
        text: messages.receiptAsExpensePairedTransactionWarning,
        headerText: uiTexts.warning,
        confirm: true,
        okButtonText: uiTexts.unpair
      }).then((result) => {
        if (result) {
          Async.runInSequence(transactionIds.map((id) => {
            return () => dispatch(BusinessesActions.unpairMatches(id, documentId));
          })).then(() => refetchDocumentData());
        } else {
          setDocumentState({ exportAs: AdvancedTransactionTypes.BILL });
        }
      });
    }
  }, [documentId, transactionIds, exportAs, uiTexts, messages, setDocumentState, showCommonModal, dispatch, refetchDocumentData]);

  return (
    <>
      {!!advancedDocumentsWorkflow && <PairedTransactionsField />}
      <TypeField />
      {!businessUser && type === DocumentTypes.RECEIPT && quickBooksBusiness && <ExportAsField />}
      <IdField />
      <CurrencyField />
      <AmountBaseField />
      {detailedTaxRates.length
        ? detailedTaxRates.map(({ id, name }) => (
          <DetailedTaxRateField key={id} id={id} name={name} />
        ))
        : <AmountVatField />}
      <AmountField />
      <IssueDateField />
      {(![DocumentTypes.RECEIPT, DocumentTypes.SALES_RECEIPT].includes(type)) && <DueDateField />}
      {!businessUser && (!!(((xeroBusiness || zohoBusiness) && !paidTransactions.length)
        || (quickBooksBusiness && (type === DocumentTypes.RECEIPT) && exportAs === AdvancedTransactionTypes.EXPENSE))) && (
        <>
          <PayToField />
          {(xeroBusiness || zohoBusiness) && <PaymentDateField />}
        </>
      )}
      <AddressField />
      {showVatIdField && <VatIdField />}
      {!!locations.length && <LocationField />}
      {!!quickBooksBusiness && <MemoField />}
    </>
  );
};

export default React.memo(Fields);
